import { c as _c } from "react/compiler-runtime";
import { useRef, type RefObject } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import { styled } from '@classify/react-core-components/styles';
import Typography from '@classify/react-core-components/Typography';
import BackLink from '@classify/react-core-components/BackLink';
import CircularLearningProgress from '@classify/react-product-components/CircularLearningProgress';
import { getPathName, ROUTES } from '@openclassrooms/routing';
import { translate } from '@openclassrooms/translations';
import { useCurrentUserContext } from '@openclassrooms/data-connect-web/users';
import { useChapterContext } from '../_contexts/ChapterContext';
const Root = styled(AppBar)(({
  theme: {
    palette,
    zIndex
  }
}) => ({
  top: 0,
  boxShadow: `inset 0px -1px 0px ${palette.status.neutral}`,
  zIndex: zIndex.appBar - 1,
  backgroundColor: palette.layout.background.navigation
}));
const HeaderToolbar = styled(Toolbar, {
  shouldForwardProp: prop => prop !== 'isExtended'
})<{
  isExtended?: boolean;
}>(({
  theme: {
    spacing,
    breakpoints
  },
  isExtended
}) => ({
  ...(isExtended ? {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: spacing(2),
    minHeight: 66,
    alignItems: 'self-start',
    [breakpoints.up('sm')]: {
      padding: spacing(3, 4)
    },
    [breakpoints.up('lg')]: {
      padding: spacing(3, 12)
    }
  } : {
    padding: spacing(1, 2),
    [breakpoints.up('sm')]: {
      padding: spacing(0, 4)
    },
    [breakpoints.up('lg')]: {
      padding: spacing(0, 12)
    }
  })
}));
const SubContent = styled('div')(({
  theme: {
    spacing,
    breakpoints
  }
}) => ({
  display: 'flex',
  flexDirection: 'column',
  flexWrap: 'wrap',
  marginLeft: '30px',
  '& > *': {
    marginTop: spacing(1)
  },
  [breakpoints.up('sm')]: {
    alignItems: 'center',
    flexDirection: 'row'
  }
}));
const LearningProgressContainer = styled('div')({
  display: 'flex',
  alignItems: 'center'
});
const StatusPassedLabel = styled(Typography)(({
  theme: {
    palette
  }
}) => ({
  color: palette.status.success
}));
interface Props {
  headerRef?: RefObject<HTMLDivElement | null>;
}
const CourseChapterHeader = t0 => {
  const $ = _c(20);
  const {
    headerRef
  } = t0;
  const {
    course
  } = useChapterContext();
  const {
    userId
  } = useCurrentUserContext();
  const searchParams = useSearchParams();
  const localRef = useRef(null);
  const finalRef = headerRef || localRef;
  if (!course) {
    return null;
  }
  const {
    userCourseCompletionRate: progression,
    learningActivityStatus: status
  } = course;
  const isExtended = status !== "not started" && Boolean(userId);
  const shouldShowProgression = status !== "passed";
  const t1 = course.slug ? `${course.id}-${course.slug}` : `${course.id}`;
  let t2;
  if ($[0] !== t1) {
    t2 = getPathName(ROUTES.COURSES.DETAIL, {
      courseIdSlug: t1
    });
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const courseDetailRoute = t2;
  let t3;
  if ($[2] !== searchParams) {
    t3 = searchParams?.toString();
    $[2] = searchParams;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const search = t3;
  const courseLink = search ? `${courseDetailRoute}?${search}` : courseDetailRoute;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = translate("global.forms.word.back");
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== course.title || $[6] !== courseLink) {
    t5 = <BackLink component={Link} href={courseLink} aria-label={t4}>{course.title}</BackLink>;
    $[5] = course.title;
    $[6] = courseLink;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== isExtended || $[9] !== progression || $[10] !== shouldShowProgression || $[11] !== status) {
    t6 = isExtended && <SubContent>{shouldShowProgression && <><LearningProgressContainer>{progression !== 100 && <CircularLearningProgress sx={{
            pr: 1
          }} value={progression} maxValue={100} ariaHidden="true" />}<Typography variant="bodySmall">{translate("course.chapter.completion_rate", {
              completionRate: Math.floor(progression)
            }, progression)}</Typography></LearningProgressContainer></>}{status === "passed" && <StatusPassedLabel variant="subheading">{translate("course.chapter.completion_rate", {
          completionRate: 100
        }, 100)}</StatusPassedLabel>}</SubContent>;
    $[8] = isExtended;
    $[9] = progression;
    $[10] = shouldShowProgression;
    $[11] = status;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== isExtended || $[14] !== t5 || $[15] !== t6) {
    t7 = <HeaderToolbar isExtended={isExtended}>{t5}{t6}</HeaderToolbar>;
    $[13] = isExtended;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== finalRef || $[18] !== t7) {
    t8 = <Root position="relative" color="transparent" ref={finalRef}>{t7}</Root>;
    $[17] = finalRef;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  return t8;
};
export default CourseChapterHeader;